import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useVehicleCreateMutation } from "../../../apis/fleetApis";
import { IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";
import AddFleetModal from "./AddFleetModal";
import { FormValues, SelectedFilesState } from "./FleetVehiclesManagement"; 
import { T, useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
};

const AddVehiclePane = ({ open, onClose }: Props) => {
  const { t } = useLanguage();
  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      status: "ACTIVE",
    },
  });

  const { mutate: createVehicle, isPending: createPending } =
    useVehicleCreateMutation();
  const [selectedFiles, setSelectedFiles] = useState<SelectedFilesState>({
    images: false,
    imagesOdoMeter: false,
    rcDocs: false,
    inspectionPaper: false,
    registrationDocumentUrl: false,
    insuranceDocumentUrl: false,
  });

  const handleCreateVehicle = (data: any) => {
    let { rcDocs, imagesOdoMeter, images, inspectionPaper, registrationDocumentUrl, insuranceDocumentUrl, ...otherData } = data;
    const payload = {
      ...otherData,
      ...(selectedFiles?.rcDocs && { rcDocs: data?.rcDocs }),
      ...(selectedFiles?.imagesOdoMeter && {
        imagesOdoMeter: data?.imagesOdoMeter,
      }),
      ...(selectedFiles?.images && { images: data?.images }),
      ...(selectedFiles?.inspectionPaper && { inspectionPaper: data?.inspectionPaper }),
      ...(selectedFiles?.registrationDocumentUrl && { registrationDocumentUrl: data?.registrationDocumentUrl }),
      ...(selectedFiles?.insuranceDocumentUrl && { insuranceDocumentUrl: data?.insuranceDocumentUrl }),
    };

    createVehicle(payload, {
      onSuccess: () => {
        onClose();
        reset();
        window.dispatchEvent(new CustomEvent("refetchVehicles"));
        toast.success(t("Vehicle created successfully"));
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create vehicle";
        toast.error(t(errorMessage));
      },
    });
  };

  if (!open) return null;

  return (
    <div className="relative bg-white/20 rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 bg-white/50">
        <div>
          <h2 className="text-xl font-semibold text-[#212121]">
            <T>Add New Vehicle</T>
          </h2>
          <p className="text-sm text-[#212121]/70">
            <T>Enter vehicle details below</T>
          </p>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <button
            onClick={handleSubmit(handleCreateVehicle)}
            disabled={createPending}
            className="px-4 py-2 bg-[#FBD037] text-[#212121] text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {createPending ? t("Adding...") : t("Add")}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto   scrollbar-clean">
        <AddFleetModal
          register={register as any}
          errors={errors as any}
          setValue={setValue as any}
          watch={watch as any}
          clearErrors={clearErrors as any}
          control={control as any}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
        />
      </div>
    </div>
  );
};

export default AddVehiclePane;
